import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { AccountIcon } from '@/assets/icons/tab/account';
import { logoMap } from '@/components/AppNavBar';
import { LineSeparator } from '@/components/LineSeparator';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { DESKTOP_PAGE_MARGIN, MAX_DESKTOP_WEB_WIDTH } from '@/utils/constants-platform-specific';

import { useSearchHotkey } from '../hooks/use-search-hotkey';
import { DesktopNavChip } from './DesktopNavChip';
import { DesktopProductSwitcherBar } from './DesktopProductSwitcherBar';
import { DesktopSearchButton } from './DesktopSearchButton';

export type DesktopHeaderLink = {
    label: string;
    onPress: () => void;
    active?: boolean;
    testID?: string;
};

type DesktopHeaderProps = {
    links: DesktopHeaderLink[];
    /** Renders the search button and registers the Cmd/Ctrl+K hotkey when provided. */
    onSearchPress?: () => void;
    /** Product-specific right-side actions (e.g. balance + currency toggle), rendered before the account button. */
    actions?: ReactNode;
};

/**
 * Generic desktop web header shell: product switcher strip, active product logo, nav links
 */
export const DesktopHeader = ({ links, onSearchPress, actions }: DesktopHeaderProps) => {
    const navigation = useNavigation();
    const { t } = useTranslation('desktop_nav');
    const activeProduct = useJurisdictionStore(state => state.product);

    useSearchHotkey(onSearchPress);

    return (
        <Box width="100%" zIndex={120}>
            <DesktopProductSwitcherBar />
            <Box
                width="100%"
                style={styles.pageMargin}
                alignItems="center"
                backgroundColor="background.neutral.base"
                borderBottomWidth={1}
                borderBottomColor={ds.colorToken.border.strong}
            >
                <Row
                    height={72}
                    width="100%"
                    maxWidth={MAX_DESKTOP_WEB_WIDTH}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Row gap="s12" alignItems="center">
                        {activeProduct !== Product.None ? logoMap[activeProduct] : null}
                        <Row gap="s4" alignItems="center">
                            {links.map(link => (
                                <DesktopNavChip
                                    key={link.label}
                                    label={link.label}
                                    active={link.active}
                                    onPress={link.onPress}
                                    testID={link.testID}
                                />
                            ))}
                            {onSearchPress ? (
                                <>
                                    <Box px="s10">
                                        <LineSeparator vertical style={styles.divider} />
                                    </Box>
                                    <DesktopSearchButton
                                        onPress={onSearchPress}
                                        accessibilityLabel={t('search')}
                                        testID="desktop-nav-search"
                                    />
                                </>
                            ) : null}
                        </Row>
                    </Row>
                    <Row gap="s12" alignItems="center">
                        {actions}
                        <DesktopNavChip
                            icon={<AccountIcon width={20} height={20} />}
                            onPress={() =>
                                navigation.navigate('AccountHome', {
                                    screen: 'Account',
                                    params: { mode: 'stack' },
                                })
                            }
                            accessibilityLabel={t('account')}
                            testID="desktop-nav-account"
                        />
                    </Row>
                </Row>
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    divider: {
        height: 20,
    },
    pageMargin: {
        paddingHorizontal: DESKTOP_PAGE_MARGIN,
    },
});
